import React from 'react';
import type { DropResult } from './types';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { DndA11yProvider, useDndA11yContext } from './DndA11yContext';

export interface DragDropContextProps {
  children: any;
  onDragStart?: () => void;
  onDragEnd?: (result: DropResult) => void;
  keyboardDroppableOrder?: string[];
}

export function DragDropContext(props: DragDropContextProps) {
  return (
    <DndA11yProvider droppableOrder={props.keyboardDroppableOrder} emitDragEnd={(result) => props.onDragEnd?.(result)}>
      <DragDropContextImpl {...props} />
    </DndA11yProvider>
  );
}

function DragDropContextImpl(props: DragDropContextProps) {
  const a11y = useDndA11yContext();

  React.useEffect(() => {
    return monitorForElements({
      onDragStart: () => {
        props.onDragStart?.();
      },
      onDrop: ({ source, location }: any) => {
        const draggableId = (source.data as any)?.draggableId as string | undefined;
        const sourceDroppableId = (source.data as any)?.sourceDroppableId as string | undefined;

        const dropTargets = location.current.dropTargets;
        const primaryDropTarget = dropTargets.length ? dropTargets[dropTargets.length - 1] : null;
        const destinationDroppableId = (primaryDropTarget?.data as any)?.droppableId as string | undefined;

        if (!draggableId || !sourceDroppableId) {
          return;
        }

        const dropResult: DropResult = {
          draggableId,
          source: { droppableId: sourceDroppableId, index: 0 },
          destination: destinationDroppableId ? { droppableId: destinationDroppableId, index: 0 } : null,
        };

        props.onDragEnd?.(dropResult);

        if (dropResult.destination && dropResult.destination.droppableId !== dropResult.source.droppableId) {
          a11y?.flashMoved(draggableId);
        }
      },
    });
  }, [props, a11y]);

  return <>{props.children as any}</>;
}
